// src/stores/useTasks.ts
import { create } from 'zustand'
import { supabase } from '../services/supabase'
import { api } from '../services/api'
import type { Task } from '../types'
import { useSettings } from './useSettings'

interface TasksStore {
  tasks: Task[]
  loading: boolean
  load: () => Promise<void>
  add: (text: string) => Promise<Task | null>
  classify: (id: string, type: 'signal' | 'noise', reason: string | null, q1?: boolean, q2?: boolean, q3?: boolean) => Promise<void>
  markDone: (id: string, done: boolean) => Promise<void>
  delete: (id: string) => Promise<void>
  setEstimate: (id: string, mins: number | null) => Promise<void>
  setRecurrence: (id: string, r: 'none' | 'daily' | 'weekly') => Promise<void>
  startTimer: (id: string) => Promise<void>
  stopTimer: (id: string) => Promise<void>
  _tick: () => void
  initRealtime: () => void
  _mergeInsert: (task: Task) => void
  _mergeUpdate: (task: Task) => void
  _mergeDelete: (id: string) => void
  _timerInterval: ReturnType<typeof setInterval> | null
  _tickCount: number
}

export const useTasks = create<TasksStore>((set, get) => ({
  tasks: [],
  loading: false,
  _timerInterval: null,
  _tickCount: 0,

  load: async () => {
    set({ loading: true })
    try {
      const today = new Date().toISOString().split('T')[0]
      const { data, error } = await supabase
        .from('tasks')
        .select('*')
        .eq('date', today)
        .order('created_at', { ascending: false })
      if (error) throw error
      set({ tasks: (data as Task[]) || [] })
    } catch (error) {
      console.error('Error loading tasks:', error)
    } finally {
      set({ loading: false })
    }
  },

  add: async (text: string) => {
    const { data: { session } } = await supabase.auth.getSession()
    if (!session) return null

    const tempId = `tmp-${Date.now()}`
    const newTask: Task = {
      id: tempId,
      user_id: session.user.id,
      text,
      type: 'unclassified',
      done: false,
      ai_reason: null,
      ai_q1: null,
      ai_q2: null,
      ai_q3: null,
      timer_seconds: 0,
      progress: 0,
      timer_started_at: null,
      timer_running: false,
      date: new Date().toISOString().split('T')[0],
      created_at: new Date().toISOString(),
      done_at: null,
      estimated_mins: null,
      recurrence: 'none',
    }

    set({ tasks: [newTask, ...get().tasks] })

    try {
      const { data, error } = await supabase
        .from('tasks')
        .insert({ user_id: session.user.id, text, type: 'unclassified', date: newTask.date })
        .select()
        .single()
      if (error) throw error

      set({ tasks: get().tasks.map(t => t.id === tempId ? data : t) })

      // Background auto-classify
      const s = useSettings.getState().settings
      if (s.ai_classification && (s.is_premium || s.user_api_key)) {
        ;(async () => {
          try {
            const result = await api.classify(text)
            await get().classify(data.id, result.type as 'signal' | 'noise', result.reason ?? null, result.q1, result.q2, result.q3)
          } catch (e) {
            console.warn('[tasks] background classify failed:', e)
          }
        })()
      }

      return data
    } catch (error) {
      console.error('Error adding task:', error)
      set({ tasks: get().tasks.filter(t => t.id !== tempId) })
      return null
    }
  },

  classify: async (id, type, reason, q1, q2, q3) => {
    const oldTasks = get().tasks
    set({ tasks: get().tasks.map(t => t.id === id ? { ...t, type, ai_reason: reason, ai_q1: q1 ?? null, ai_q2: q2 ?? null, ai_q3: q3 ?? null } : t) })
    try {
      const { error } = await supabase.from('tasks').update({ type, ai_reason: reason, ai_q1: q1, ai_q2: q2, ai_q3: q3 }).eq('id', id)
      if (error) throw error
    } catch (error) {
      console.error('Error classifying task:', error)
      set({ tasks: oldTasks })
    }
  },

  markDone: async (id, done) => {
    const oldTasks = get().tasks
    const doneAt = done ? new Date().toISOString() : null
    set({ tasks: get().tasks.map(t => t.id === id ? { ...t, done, done_at: doneAt } : t) })
    try {
      const { error } = await supabase.from('tasks').update({ done, done_at: doneAt }).eq('id', id)
      if (error) throw error
    } catch (error) {
      console.error('Error marking task done:', error)
      set({ tasks: oldTasks })
    }
  },

  delete: async (id) => {
    const oldTasks = get().tasks
    set({ tasks: get().tasks.filter(t => t.id !== id) })
    try {
      const { error } = await supabase.from('tasks').delete().eq('id', id)
      if (error) throw error
    } catch (error) {
      console.error('Error deleting task:', error)
      set({ tasks: oldTasks })
    }
  },

  setEstimate: async (id, mins) => {
    const oldTasks = get().tasks
    set({ tasks: get().tasks.map(t => t.id === id ? { ...t, estimated_mins: mins } : t) })
    try {
      const { error } = await supabase.from('tasks').update({ estimated_mins: mins }).eq('id', id)
      if (error) throw error
    } catch (error) {
      console.error('Error setting estimate:', error)
      set({ tasks: oldTasks })
    }
  },

  setRecurrence: async (id, r) => {
    const oldTasks = get().tasks
    set({ tasks: get().tasks.map(t => t.id === id ? { ...t, recurrence: r } : t) })
    try {
      const { error } = await supabase.from('tasks').update({ recurrence: r }).eq('id', id)
      if (error) throw error
    } catch (error) {
      console.error('Error setting recurrence:', error)
      set({ tasks: oldTasks })
    }
  },

  startTimer: async (id) => {
    const startedAt = new Date().toISOString()
    const oldTasks = get().tasks
    const task = get().tasks.find(t => t.id === id)
    set({ tasks: get().tasks.map(t => t.id === id ? { ...t, timer_running: true, timer_started_at: startedAt } : t) })
    try {
      const { error } = await supabase.from('tasks').update({ timer_running: true, timer_started_at: startedAt }).eq('id', id)
      if (error) throw error
      if (task) {
        import('./useSquads').then(m => m.useSquads.getState().broadcastPresence(task.text)).catch(() => {})
      }
    } catch (error) {
      console.error('Error starting timer:', error)
      set({ tasks: oldTasks })
    }
  },

  stopTimer: async (id) => {
    const task = get().tasks.find(t => t.id === id)
    const finalSeconds = task?.timer_seconds ?? 0
    const oldTasks = get().tasks
    set({ tasks: get().tasks.map(t => t.id === id ? { ...t, timer_running: false, timer_started_at: null } : t) })
    try {
      const { error } = await supabase.from('tasks').update({ timer_running: false, timer_started_at: null, timer_seconds: finalSeconds }).eq('id', id)
      if (error) throw error
      import('./useSquads').then(m => m.useSquads.getState().clearPresence()).catch(() => {})
    } catch (error) {
      console.error('Error stopping timer:', error)
      set({ tasks: oldTasks })
    }
  },

  _tick: () => {
    const { tasks, _tickCount } = get()
    if (!tasks.some(t => t.timer_running)) return

    const newTasks = tasks.map(t => {
      if (!t.timer_running) return t
      const newSeconds = t.timer_seconds + 1
      return { ...t, timer_seconds: newSeconds, progress: Math.min(100, (newSeconds / 3600) * 100) }
    })

    set({ tasks: newTasks, _tickCount: _tickCount + 1 })

    // Persist every 30s
    if ((_tickCount + 1) % 30 === 0) {
      newTasks.filter(t => t.timer_running).forEach(t => {
        void Promise.resolve(supabase.from('tasks').update({ timer_seconds: t.timer_seconds }).eq('id', t.id)).catch(() => {})
      })
    }
  },

  initRealtime: () => {
    if (get()._timerInterval) clearInterval(get()._timerInterval!)
    const interval = setInterval(() => get()._tick(), 1000)
    set({ _timerInterval: interval })
  },

  _mergeInsert: (task) => {
    if (get().tasks.some(t => t.id === task.id)) return
    set({ tasks: [task, ...get().tasks] })
  },
  _mergeUpdate: (task) => {
    set({ tasks: get().tasks.map(t => t.id === task.id ? { ...t, ...task } : t) })
  },
  _mergeDelete: (id) => {
    set({ tasks: get().tasks.filter(t => t.id !== id) })
  },
}))
